import { Badge, Box, SimpleGrid, Text } from '@mantine/core';
import type { ReactElement } from 'react';
import StatBand from '@/design/StatBand';
import { SurfaceCard } from '@/design/SurfaceCard';
import { formatPercent } from '@/shared/analytics/MetricFormatting';
import type { WinEngine, WinHighlight } from './InsightsTypes';

interface InsightsWinsSectionProps {
    wins: WinEngine;
}

function WinCard({ win }: { win: WinHighlight }): ReactElement {
    const tokens = { solid: 'var(--bm-color-brand-accent)', soft: 'var(--bm-color-brand-soft)', text: 'var(--bm-color-brand-accent)', border: 'var(--bm-color-brand-border)' };
    return (
        <SurfaceCard $padding="md">
            <Box mb="sm">
                <Badge variant="light" style={{ background: tokens.soft, color: tokens.text, border: `1px solid ${tokens.border}` }}>
                    {win.category}
                </Badge>
            </Box>
            {win.promptExcerpt && (
                <Text size="sm" ff="monospace" p="sm" style={{ borderLeft: `3px solid ${tokens.solid}`, background: 'var(--bm-color-surface-alt)', borderRadius: 'var(--bm-radius)', lineHeight: 1.5 }}>
                    “{win.promptExcerpt}”
                </Text>
            )}
            <Text size="sm" c="dimmed" mt={win.promptExcerpt ? 'sm' : 0} style={{ lineHeight: 1.55 }}>
                {win.analysis}
            </Text>
        </SurfaceCard>
    );
}

/** Highlights — positive reinforcement: efficiency stats and the Wall of Wins. */
export default function InsightsWinsSection({ wins }: InsightsWinsSectionProps): ReactElement {
    return (
        <Box>
            <StatBand
                stats={[
                    {
                        label: 'One-shot success',
                        value: formatPercent(wins.oneShotRate / 100),
                        hint: `dept avg ${formatPercent(wins.deptAvgOneShotRate / 100)} · ${wins.oneShotDelta >= 0 ? '+' : ''}${wins.oneShotDelta}% vs prior`,
                        info: 'Share of sessions resolved in a single prompt — higher means clearer, well-scoped asks.',
                    },
                    {
                        label: 'Complex tasks resolved',
                        value: `${wins.complexTasksResolved}`,
                        info: 'Multi-turn sessions (5+ prompts) you worked all the way through — deeper, harder work.',
                    },
                    {
                        label: 'Flag-free days',
                        value: `${wins.complianceStreakDays} days`,
                        hint: 'active days with no sensitivity flags',
                        info: 'Active days where no prompt was flagged for sensitive content (PII, credentials, data exposure).',
                    },
                ]}
            />
            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md" mt="md">
                {wins.wins.map((win) => (
                    <WinCard key={win.id} win={win} />
                ))}
            </SimpleGrid>
        </Box>
    );
}
